import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Upload, Paperclip, Calendar, CheckCircle2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useQuote } from "./QuoteContext";
import { PRODUCTS, PRODUCT_KEYS } from "./quote-configs";
import RgpdConsent from "./RgpdConsent";
import CalendlyInline from "./CalendlyInline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic";
const MAX_BYTES = 10 * 1024 * 1024;

export default function QuotePopup() {
  const { open, closeQuote, product, setProduct, prefill } = useQuote();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  // Honeypot: hidden field that real users never see/touch.
  // If a bot fills this we silently abort submission.
  const [hp, setHp] = useState("");

  useEffect(() => {
    if (open) {
      setStep(0);
      // Filter out empty/null prefill values so they don't accidentally validate steps
      const cleaned = Object.fromEntries(
        Object.entries(prefill || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined),
      );
      setAnswers(cleaned);
      setFiles({});
      setSuccess(false);
    }
  }, [open, product, prefill]);

  const config = product ? PRODUCTS[product] : null;
  const steps = config?.steps || [];
  const totalSteps = steps.length;
  const currentStep = steps[step];

  const setVal = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));
  const setFile = (key, file) => {
    if (file && file.size > MAX_BYTES) {
      toast.error("Fichier trop volumineux (10 Mo maximum).");
      return;
    }
    setFiles((f) => ({ ...f, [key]: file }));
  };

  const validateStep = () => {
    if (!currentStep) return false;
    const t = currentStep.type;
    const k = currentStep.key;
    if (t === "rgpd") return answers[k] === true;
    if (t === "file") return true; // optional
    if (t === "contact") {
      return !!(answers.firstname && answers.email && answers.phone);
    }
    if (t === "duo") {
      return !!(answers[currentStep.a.key] || answers[currentStep.b.key]);
    }
    if (t === "slider") return answers[k] !== undefined;
    if (t === "options") return !!answers[k];
    if (t === "select") return !!answers[k];
    if (t === "text" || t === "number" || t === "date") return !!answers[k];
    return true;
  };

  const next = () => {
    if (!validateStep()) {
      const t = currentStep.type;
      if (t === "rgpd") toast.error("Acceptez le RGPD pour continuer.");
      else if (t === "contact") toast.error("Renseignez prénom, téléphone et email.");
      else toast.error("Veuillez compléter cette étape.");
      return;
    }
    setStep((s) => Math.min(s + 1, totalSteps - 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!validateStep()) {
      toast.error("Acceptez le RGPD pour continuer.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("product", product);
      fd.append("product_label", config.title);
      fd.append("firstname", answers.firstname || "");
      fd.append("phone", answers.phone || "");
      fd.append("email", answers.email || "");
      fd.append("rgpd", "true");
      // Consentement positif au recontact (case 2, non pré-cochée — facultative)
      fd.append("recontact_consent", answers.recontact_consent === true ? "true" : "false");
      // Horodatage du consentement (preuve exigée par la loi)
      fd.append("consent_at", new Date().toISOString());
      // Anti-bot honeypot — must remain empty for real users
      fd.append("website", hp);
      // separate file fields from regular answers
      const detailEntries = { ...answers };
      delete detailEntries.firstname;
      delete detailEntries.phone;
      delete detailEntries.email;
      delete detailEntries.rgpd;
      delete detailEntries.recontact_consent;
      fd.append("details_json", JSON.stringify(detailEntries));
      // attach uploaded files
      Object.entries(files).forEach(([k, v]) => {
        if (v) fd.append(k, v);
      });
      await axios.post(`${API}/forms/multi-quote`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess(true);
    } catch (e) {
      toast.error("Erreur lors de l'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const isLast = step === totalSteps - 1;

  return (
    <div
      data-testid="quote-popup-backdrop"
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={closeQuote}
    >
      <div
        data-testid="quote-popup"
        className="relative w-full max-w-2xl max-h-[94vh] overflow-auto bg-white rounded-2xl shadow-[0_40px_80px_rgba(0,0,0,0.3)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="quote-popup-close"
          onClick={closeQuote}
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-brand-sand-2 transition-colors"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Honeypot — invisible to humans, attracts bots. Must stay empty. */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
          aria-hidden="true"
          data-testid="quote-honeypot"
          style={{
            position: "absolute",
            left: "-10000px",
            top: "auto",
            width: "1px",
            height: "1px",
            overflow: "hidden",
            opacity: 0,
            pointerEvents: "none",
          }}
        />

        {/* SUCCESS SCREEN */}
        {success ? (
          <SuccessScreen
            productLabel={config?.title}
            onClose={closeQuote}
          />
        ) : !product ? (
          <ProductPicker onPick={(k) => setProduct(k)} />
        ) : (
          <div className="px-6 sm:px-10 md:px-12 pt-12 pb-8">
            <div className="overline mb-2">{config.title}</div>
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => setProduct(null)}
                className="text-xs font-semibold text-brand-ink/60 hover:text-brand-ink"
                data-testid="back-to-picker"
              >
                ← Changer de produit
              </button>
              <div className="text-xs text-brand-ink/60 font-semibold ml-auto">
                Étape {step + 1}/{totalSteps}
              </div>
            </div>

            <div className="h-1 bg-brand-sand-2 rounded-full overflow-hidden mb-8">
              <motion.div
                className="h-full bg-brand-ink"
                initial={false}
                animate={{ width: `${((step + 1) / totalSteps) * 100}%` }}
                transition={{ ease: "easeOut", duration: 0.4 }}
              />
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.28 }}
              >
                <StepRenderer
                  step={currentStep}
                  answers={answers}
                  files={files}
                  onChange={setVal}
                  onFile={setFile}
                />
              </motion.div>
            </AnimatePresence>

            <div className="mt-8 flex items-center justify-between gap-4">
              <button
                onClick={prev}
                disabled={step === 0}
                data-testid="msf-prev"
                className="inline-flex items-center gap-1 text-sm font-semibold text-brand-ink/60 hover:text-brand-ink disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" /> Retour
              </button>
              {!isLast ? (
                <button
                  onClick={next}
                  data-testid="msf-next"
                  className="btn-press inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-black text-white px-6 py-3 text-sm font-semibold tracking-[0.15em] uppercase"
                >
                  Continuer <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="msf-submit"
                  className="btn-press inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-black text-white px-6 py-3 text-sm font-semibold tracking-[0.15em] uppercase disabled:opacity-60"
                >
                  {submitting ? "Envoi…" : "Envoyer ma demande"} <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────── PRODUCT PICKER ───────────────
function ProductPicker({ onPick }) {
  return (
    <div className="px-6 sm:px-10 md:px-12 pt-12 pb-10" data-testid="product-picker">
      <div className="overline mb-2">Devis gratuit, sans engagement</div>
      <h3 className="font-serif text-3xl md:text-4xl tracking-tight text-brand-ink">
        Quel devis souhaitez-vous&nbsp;?
      </h3>
      <p className="text-brand-ink/60 mt-2 text-sm">
        Sélectionnez un produit, je vous contacte sous 24 h.
      </p>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {PRODUCT_KEYS.map((k) => {
          const p = PRODUCTS[k];
          const Icon = p.icon;
          return (
            <button
              key={k}
              onClick={() => onPick(k)}
              data-testid={`pick-product-${k}`}
              className="card-lift group flex flex-col items-start gap-3 rounded-xl border border-brand-border bg-white hover:border-brand-ink p-5 text-left transition-colors"
            >
              <div className="h-10 w-10 rounded-full bg-brand-ink text-white flex items-center justify-center group-hover:bg-black">
                <Icon className="h-4 w-4" />
              </div>
              <div className="font-serif text-[15px] leading-tight text-brand-ink">{p.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────── SUCCESS SCREEN ───────────────
function SuccessScreen({ productLabel, onClose }) {
  return (
    <div className="px-6 sm:px-10 md:px-12 py-14 text-center" data-testid="msf-success">
      <div className="mx-auto h-16 w-16 rounded-full bg-brand-ink text-white flex items-center justify-center">
        <CheckCircle2 className="h-7 w-7" />
      </div>
      <div className="overline mt-6">{productLabel}</div>
      <h3 className="font-serif text-3xl md:text-4xl tracking-tight mt-3 text-brand-ink leading-tight">
        Votre demande a bien été reçue.
      </h3>
      <p className="mt-4 text-brand-ink/65 max-w-md mx-auto leading-relaxed">
        Je vous recontacte sous 24 à 48 h. <strong>Pour aller plus vite</strong>, choisissez
        directement un créneau dans mon agenda ci-dessous.
      </p>

      {/* Widget Calendly inline directement intégré en fin de tunnel */}
      <div className="mt-8 -mx-2 sm:-mx-4 md:-mx-6 rounded-2xl overflow-hidden border border-brand-border bg-white">
        <CalendlyInline minHeight={620} testId="msf-calendly-widget" />
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={onClose}
          className="btn-press inline-flex items-center justify-center gap-2 rounded-full bg-white border border-brand-border hover:border-brand-ink text-brand-ink px-7 py-3.5 text-[12px] font-semibold tracking-[0.2em] uppercase"
        >
          Fermer
        </button>
      </div>

      {/* Pop-up WhatsApp uniquement en fin de tunnel — bandeau bas, jamais ailleurs */}
      <div
        data-testid="msf-whatsapp-end"
        className="mt-10 pt-6 border-t border-brand-border flex items-center justify-center gap-3 text-sm text-brand-ink/70"
      >
        <span className="inline-flex h-9 w-9 rounded-full bg-[#25D366] text-white items-center justify-center shrink-0">
          <svg viewBox="0 0 32 32" fill="currentColor" className="h-4 w-4" aria-hidden="true">
            <path d="M16 .5C7.44.5.5 7.44.5 16c0 2.82.74 5.48 2.04 7.78L.5 31.5l7.94-2c2.22 1.22 4.78 1.92 7.56 1.92C24.56 31.42 31.5 24.48 31.5 15.92 31.5 7.44 24.56.5 16 .5Zm7.08 19.26c-.38-.2-2.28-1.12-2.64-1.26-.36-.14-.62-.2-.88.2-.26.38-1 1.26-1.22 1.52-.22.26-.46.28-.84.1-.38-.2-1.62-.6-3.08-1.9-1.14-1.02-1.9-2.28-2.12-2.66-.22-.38-.02-.58.16-.78.16-.16.38-.42.56-.64.2-.22.26-.38.4-.62.14-.26.06-.48-.02-.68-.08-.2-.88-2.12-1.2-2.9-.32-.78-.64-.68-.88-.68h-.76c-.26 0-.68.1-1.04.48-.36.38-1.38 1.36-1.38 3.3 0 1.94 1.4 3.82 1.6 4.08.2.26 2.76 4.2 6.68 5.9 3.92 1.7 3.92 1.14 4.62 1.08.7-.06 2.28-.94 2.6-1.84.32-.9.32-1.66.22-1.84-.1-.18-.36-.28-.74-.48Z" />
          </svg>
        </span>
        <span>
          Une question avant d'envoyer ?{" "}
          <a
            href="https://wa.me/33615907316?text=Bonjour%2C%20j%27ai%20une%20question%20avant%20d%27envoyer%20mon%20devis."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="msf-whatsapp-link"
            className="font-semibold underline decoration-[#C8A96E] underline-offset-4 hover:text-brand-ink transition-colors"
          >
            Discutons sur WhatsApp
          </a>
        </span>
      </div>
    </div>
  );
}

// ─────────────── STEP RENDERER ───────────────
function StepRenderer({ step, answers, files, onChange, onFile }) {
  const t = step.type;
  return (
    <div>
      {t !== "contact" && t !== "rgpd" && (
        <div className="font-serif text-2xl md:text-[28px] tracking-tight leading-tight mb-2 text-brand-ink">
          {step.label}
        </div>
      )}
      {step.hint && t !== "file" && (
        <div className="text-xs text-brand-ink/55 mb-6">{step.hint}</div>
      )}
      {!step.hint && t !== "contact" && t !== "rgpd" && t !== "file" && <div className="mb-6" />}

      {t === "text" && (
        <input
          type="text"
          value={answers[step.key] || ""}
          onChange={(e) => onChange(step.key, e.target.value)}
          placeholder={step.placeholder}
          data-testid={`msf-${step.key}`}
          className="w-full rounded-lg border border-brand-border bg-white px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-brand-ink/40"
        />
      )}

      {t === "number" && (
        <div className="relative">
          <input
            type="number"
            value={answers[step.key] ?? ""}
            onChange={(e) => onChange(step.key, e.target.value === "" ? "" : Number(e.target.value))}
            placeholder={step.placeholder}
            step={step.decimal ? (step.step || 0.01) : 1}
            data-testid={`msf-${step.key}`}
            className="w-full rounded-lg border border-brand-border bg-white pl-4 pr-16 py-4 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-brand-ink/40"
          />
          {step.suffix && (
            <span className="absolute inset-y-0 right-4 flex items-center text-brand-ink/55 text-sm font-medium pointer-events-none">
              {step.suffix}
            </span>
          )}
        </div>
      )}

      {t === "date" && (
        <input
          type="date"
          value={answers[step.key] || ""}
          onChange={(e) => onChange(step.key, e.target.value)}
          data-testid={`msf-${step.key}`}
          className="w-full rounded-lg border border-brand-border bg-white px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-brand-ink/40"
        />
      )}

      {t === "slider" && (
        <SliderField step={step} answers={answers} onChange={onChange} />
      )}

      {t === "options" && (
        <div
          className={`grid gap-3 ${
            step.options.length <= 2 ? "grid-cols-2" : step.options.length === 3 ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-4"
          }`}
        >
          {step.options.map((o) => (
            <button
              key={o.v}
              onClick={() => onChange(step.key, o.v)}
              data-testid={`msf-${step.key}-${o.v}`}
              className={`py-4 px-3 rounded-xl border text-sm font-semibold transition-all ${
                answers[step.key] === o.v
                  ? "bg-brand-ink text-white border-brand-ink"
                  : "bg-white border-brand-border hover:border-brand-ink"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      )}

      {t === "select" && (
        <select
          value={answers[step.key] || ""}
          onChange={(e) => onChange(step.key, e.target.value)}
          data-testid={`msf-${step.key}`}
          className="w-full rounded-lg border border-brand-border bg-white px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-brand-ink/40 appearance-none cursor-pointer"
        >
          <option value="" disabled>
            {step.placeholder || "Sélectionnez…"}
          </option>
          {step.options.map((o) => (
            <option key={o.v} value={o.v}>
              {o.l}
            </option>
          ))}
        </select>
      )}

      {t === "duo" && (
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            type={step.a.type || "text"}
            value={answers[step.a.key] || ""}
            onChange={(e) => onChange(step.a.key, e.target.value)}
            placeholder={step.a.placeholder}
            data-testid={`msf-${step.a.key}`}
            className="w-full rounded-lg border border-brand-border bg-white px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-brand-ink/40"
          />
          <input
            type={step.b.type || "number"}
            value={answers[step.b.key] || ""}
            onChange={(e) => onChange(step.b.key, e.target.value === "" ? "" : Number(e.target.value))}
            placeholder={step.b.placeholder}
            data-testid={`msf-${step.b.key}`}
            className="w-full rounded-lg border border-brand-border bg-white px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-brand-ink/40"
          />
        </div>
      )}

      {t === "file" && (
        <FileDrop
          testid={`msf-${step.key}`}
          label={step.label}
          hint={step.hint}
          file={files[step.key]}
          onChange={(e) => onFile(step.key, e.target.files?.[0] || null)}
        />
      )}

      {t === "contact" && (
        <div>
          <div className="font-serif text-2xl md:text-[28px] tracking-tight leading-tight mb-2 text-brand-ink">
            Vos coordonnées
          </div>
          <div className="text-xs text-brand-ink/55 mb-6">
            Je vous contacte sous 24 h. Aucune donnée n'est revendue.
          </div>
          <div className="grid gap-3">
            <input
              type="text"
              placeholder="Prénom *"
              value={answers.firstname || ""}
              onChange={(e) => onChange("firstname", e.target.value)}
              data-testid="msf-firstname"
              className="w-full rounded-lg border border-brand-border bg-white px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-brand-ink/40"
            />
            <input
              type="tel"
              placeholder="Téléphone *"
              value={answers.phone || ""}
              onChange={(e) => onChange("phone", e.target.value)}
              data-testid="msf-phone"
              className="w-full rounded-lg border border-brand-border bg-white px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-brand-ink/40"
            />
            <input
              type="email"
              placeholder="Email *"
              value={answers.email || ""}
              onChange={(e) => onChange("email", e.target.value)}
              data-testid="msf-email"
              className="w-full rounded-lg border border-brand-border bg-white px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-brand-ink/40"
            />
          </div>
        </div>
      )}

      {t === "rgpd" && (
        <div>
          <div className="font-serif text-2xl md:text-[28px] tracking-tight leading-tight mb-2 text-brand-ink">
            Confidentialité et consentement
          </div>
          <RgpdConsent
            rgpdConsent={answers.rgpd === true}
            setRgpdConsent={(v) => onChange("rgpd", v)}
            recontactConsent={answers.recontact_consent === true}
            setRecontactConsent={(v) => onChange("recontact_consent", v)}
            testidPrefix="msf"
          />
        </div>
      )}
    </div>
  );
}

function SliderField({ step, answers, onChange }) {
  const value = answers[step.key] ?? step.defaultValue ?? step.min;
  // initialize on mount
  useEffect(() => {
    if (answers[step.key] === undefined) onChange(step.key, step.defaultValue ?? step.min);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const display = step.decimal ? Number(value).toFixed(2) : Math.round(value);
  return (
    <div>
      <div className="flex items-end justify-between mb-3">
        <span className="text-sm text-brand-ink/60">{step.label}</span>
        <span className="font-serif text-3xl text-brand-ink">
          {display}
          {step.unit ? ` ${step.unit}` : ""}
        </span>
      </div>
      <Slider
        min={step.min}
        max={step.max}
        step={step.step}
        value={[value]}
        onValueChange={(v) => onChange(step.key, v[0])}
        data-testid={`msf-${step.key}`}
      />
      <div className="mt-2 flex justify-between text-[11px] text-brand-ink/50">
        <span>{step.min}{step.unit ? ` ${step.unit}` : ""}</span>
        <span>{step.max}{step.unit ? ` ${step.unit}` : ""}</span>
      </div>
    </div>
  );
}

function FileDrop({ label, hint, file, onChange, testid }) {
  return (
    <label className="block cursor-pointer group">
      <div
        data-testid={`${testid}-drop`}
        className="border border-dashed border-brand-border rounded-xl px-5 py-6 hover:border-brand-ink hover:bg-brand-sand-2/40 transition-colors"
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-brand-sand-2 flex items-center justify-center shrink-0">
            {file ? <Paperclip className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-brand-ink truncate">{file ? file.name : label}</div>
            <div className="text-xs text-brand-ink/55 mt-1 leading-relaxed">{hint}</div>
            <div className="text-[11px] text-brand-ink/40 mt-2 tracking-wider uppercase">
              PDF, JPG, PNG, HEIC — 10 Mo maximum — optionnel
            </div>
          </div>
        </div>
      </div>
      <input type="file" accept={ACCEPT} className="hidden" onChange={onChange} data-testid={testid} />
    </label>
  );
}
